import { getProjects } from '@/api/projects.api';
import { getApiErrorMessage } from '@/api/base.api';
import { PROJECT_DROPDOWN_PAGE_SIZE } from '@/constants/projects.constants';
import type { PaginatedProjectsResponse, Project } from '@/interfaces/projects.interface';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Same shape as `useRef<boolean>`; avoids deprecated `MutableRefObject` in newer @types/react */
export type LoadInFlightRef = { current: boolean };

/** Coalesce identical in-flight list requests when multiple dropdowns mount together */
const inflightProjectRequests = new Map<
  string,
  Promise<PaginatedProjectsResponse>
>();

function requestKey(search: string, skip: number, pageSize: number) {
  return `${search.trim()}:${skip}:${pageSize}`;
}

function dedupeProjectsById(list: Project[]): Project[] {
  const byId = new Map<string, Project>();
  for (const p of list) {
    byId.set(p.projectId, p);
  }
  return Array.from(byId.values());
}

export interface UseProjectDropdownOptionsResult {
  /** Deduped, optionally context-seeded list of projects. */
  items: Project[];
  /** True while a `loadPage` request is in flight (React state). */
  loading: boolean;
  /**
   * Synchronous mirror of `loading`. Use this from event handlers (e.g. virtual
   * scroller `onLazyLoad`) where React state may not have committed yet.
   */
  loadInFlightRef: LoadInFlightRef;
  /** Fetch a page of projects and merge into `items`. `skip === 0` resets the list. */
  loadPage: (search: string, skip: number, pageSize: number) => Promise<void>;
  /** Append (and dedupe) projects without going through the API. */
  mergeProjects: (extra: Project[]) => void;
  /** The constant page size this hook uses; exposed to avoid magic numbers in callers. */
  pageSize: number;
  /** False when the server has reported no further pages for the current query. */
  hasMore: boolean;
}

export interface UseProjectDropdownOptionsParams {
  /** Initial items used to hydrate `items` before the first network request. */
  seedProjects?: Project[];
  /** Cached `hasMore` from context so navigation restores exact pagination state. */
  seedHasMore?: boolean;
  /**
   * On unfiltered page-1 replace, preserve this project id from the previous list
   * if the server page doesn't include it (avoids prefetch/hydrate race on refresh).
   */
  persistSelectionProjectId?: string | null;
}

/**
 * Paginated, debounce-friendly project list backing `ProjectDropdown`.
 *
 * - Coalesces concurrent identical requests across mounted dropdowns.
 * - Tracks in-flight loads both as React state (`loading`) and a synchronous ref
 *   (`loadInFlightRef`) so virtualized scroll handlers can guard properly.
 * - Always dedupes by `projectId`, including when persisting the selected
 *   project across page-1 reloads.
 */
export function useProjectDropdownOptions(
  params: UseProjectDropdownOptionsParams = {}
): UseProjectDropdownOptionsResult {
  const { seedProjects, seedHasMore, persistSelectionProjectId } = params;
  const [items, setItems] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loadInFlightRef = useRef(false);
  const pendingLoadsRef = useRef(0);

  const didSeedRef = useRef(false);
  useEffect(() => {
    if (didSeedRef.current) return;
    if (!seedProjects?.length) return;
    didSeedRef.current = true;
    setItems((prev) => (prev.length > 0 ? prev : [...seedProjects]));
    setHasMore(seedHasMore ?? seedProjects.length >= PROJECT_DROPDOWN_PAGE_SIZE);
  }, [seedProjects, seedHasMore]);

  const loadPage = useCallback(
    async (search: string, skip: number, pageSize: number) => {
      pendingLoadsRef.current += 1;
      loadInFlightRef.current = true;
      setLoading(true);
      if (skip === 0) {
        setHasMore(true);
      }
      const key = requestKey(search, skip, pageSize);
      let request = inflightProjectRequests.get(key);
      if (!request) {
        request = (async () => {
          const page = Math.floor(skip / pageSize) + 1;
          return getProjects({
            page,
            size: pageSize,
            search: search.trim() || undefined,
          });
        })();
        inflightProjectRequests.set(key, request);
        request.finally(() => {
          inflightProjectRequests.delete(key);
        });
      }
      try {
        const response = await request;
        const pageItems = Array.isArray(response.items) ? response.items : [];
        const page = Math.floor(skip / pageSize) + 1;
        const currentPage = response.page ?? page;
        const totalPages = response.pages ?? 1;
        const newHasMore = pageItems.length > 0 ? currentPage < totalPages : false;
        setHasMore(newHasMore);
        setItems((prev) => {
          const base = dedupeProjectsById(pageItems);
          if (skip !== 0) {
            return dedupeProjectsById([...prev, ...base]);
          }
          if (search.trim()) return base;
          const pid = persistSelectionProjectId?.trim();
          if (!pid || base.some((p) => p.projectId === pid)) return base;
          const keep = prev.find((p) => p.projectId === pid);
          return keep ? dedupeProjectsById([...base, keep]) : base;
        });
      } catch (err) {
        console.error(getApiErrorMessage(err, 'Failed to load projects'));
        if (skip === 0) setItems([]);
        setHasMore(false);
      } finally {
        pendingLoadsRef.current -= 1;
        if (pendingLoadsRef.current === 0) {
          loadInFlightRef.current = false;
        }
        setLoading(false);
      }
    },
    [persistSelectionProjectId]
  );

  const mergeProjects = useCallback((extra: Project[]) => {
    if (!extra.length) return;
    setItems((prev) => dedupeProjectsById([...prev, ...extra]));
  }, []);

  return {
    items,
    loading,
    loadInFlightRef,
    loadPage,
    mergeProjects,
    pageSize: PROJECT_DROPDOWN_PAGE_SIZE,
    hasMore,
  };
}
